import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  IconButton as MuiIconButton,
  TextField as MuiTextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import * as Sentry from '@sentry/react';
import { MemberService } from '../../services/member/memberService.ts';

interface ChangeMemberPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': {
      borderColor: 'var(--color-secondary)',
    },
  },
};

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .required('Please confirm password')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
});

export const ChangeMemberPasswordDialog: React.FC<ChangeMemberPasswordDialogProps> = ({ open, onClose, userId }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const memberService = new MemberService();

  const initialValues = {
    newPassword: '',
    confirmPassword: '',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
        paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
      }}
      sx={{ padding: 0 }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t('members.changePassword')}
        <MuiIconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </MuiIconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const payload = {
                userId,
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword,
              };
              await memberService.changeMemberPassword(payload);
              notify(t('members.changePasswordDialog.passwordChangedSuccess') || 'Password changed successfully', { type: 'success' });
              resetForm();
              onClose();
            } catch (error: any) {
              if (error.code === 'auth/weak-password') {
                notify(t('members.changePasswordDialog.weakPassword') || 'Password is too weak', { type: 'error' });
              } else if (error.code === 'auth/requires-recent-login') {
                notify(t('members.changePasswordDialog.requiresRecentLogin') || 'Please log in again to change password', { type: 'error' });
              } else {
                notify(error.message || 'Failed to change password', { type: 'error' });
                Sentry.captureException(error);
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                <MuiTextField
                  name="newPassword"
                  type="password"
                  label=""
                  placeholder={t('members.changePasswordDialog.newPassword') || 'New Password'}
                  sx={inputSx}
                  size="small"
                  fullWidth
                  value={values.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={(touched.newPassword && errors.newPassword) || " "}
                />

                <MuiTextField
                  name="confirmPassword"
                  type="password"
                  label=""
                  placeholder={t('members.changePasswordDialog.confirmNewPassword') || 'Confirm New Password'}
                  sx={inputSx}
                  size="small"
                  fullWidth
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={(touched.confirmPassword && errors.confirmPassword) || " "}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={onClose}
                    sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                  >
                    {t('common.cancel')}
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-text-light)',
                      boxShadow: 'none',
                      '&:hover': {
                        cursor: 'pointer',
                        backgroundColor: 'var(--color-primary-hover)',
                        boxShadow: 'var(--shadow-1)',
                      },
                      '&:disabled': {
                        cursor: 'default',
                        backgroundColor: 'var(--color-button-background-disabled)',
                        color: 'var(--color-button-text-disabled)',
                      },
                    }}
                  >
                    {t('common.saveChanges')}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};